<template>
    <div>
        <div class="offer-users-container">
            <div v-if="!isSmartphoneLarge" class="offer-users--item offer-users--item__header">
                <div class="offer-users--item__user">
                    {{ $t('components.availability.add_availability.user_availability.users') }}
                </div>
                <div class="offer-users--item__action">
                    <p>{{ $t('components.availability.add_availability.user_availability.performer') }}</p>
                    <p>{{ $t('components.availability.add_availability.user_availability.admin') }}</p>
                </div>
            </div>
            <common-content-spacer height="medium">
                <div v-for="user in selectedUsers" :key="user?.user?.id" class="offer-users--item">
                    <div class="offer-users--item__user">
                        <common-user-avatar
                            class="availability-summary__slot--user-avatar"
                            size="medium"
                            :user="user.user"
                        ></common-user-avatar>
                        <p v-if="user.user?.firstName" class="offer-users--item__user--name">
                            {{ user.user?.firstName }} {{ user.user?.lastName }}
                        </p>
                        <p v-else class="offer-users--item__user--name">{{ user.user?.email }}</p>
                    </div>
                    <div class="offer-users--item__action">
                        <div>
                            <p v-if="isSmartphoneLarge">
                                {{ $t('components.availability.add_availability.user_availability.performer') }}
                            </p>
                            <p>
                                <checkbox-input
                                    type="checkbox"
                                    labelPosition="left"
                                    :isDisabled="selectedUsers.length === 1"
                                    :value="user.user.isPerformer"
                                    @change="toggleUserPerfomer(user)"
                                ></checkbox-input>
                            </p>
                        </div>
                        <div>
                            <p v-if="isSmartphoneLarge">
                                {{ $t('components.availability.add_availability.user_availability.admin') }}
                            </p>
                            <p>
                                <checkbox-input
                                    type="checkbox"
                                    labelPosition="left"
                                    :isDisabled="user.user.id === $auth.getUser().id"
                                    :value="user.user.isModerator"
                                    @change="toggleUserModerator(user)"
                                ></checkbox-input>
                            </p>
                        </div>
                    </div>
                </div>

                <div v-if="inviteUsers.length">
                    <p class="offer-users--item offer-users--item__header">
                        {{ $t('components.availability.add_availability.user_availability.new_users') }}
                    </p>
                    <div v-for="(user, index) in inviteUsers" :key="index" class="offer-users--item">
                        <div class="offer-users--item__user offer-users--item__user--invite">
                            <div class="offer-users--item__user--info">
                                <common-user-avatar
                                    class="availability-summary__slot--user-avatar"
                                    size="medium"
                                    :user="user"
                                ></common-user-avatar>
                                <span v-if="user.invitedUser?.id">
                                    {{ user.invitedUser?.firstName }}
                                    {{ user.invitedUser?.lastName }}
                                </span>
                                <span v-else>{{ user.email }}</span>
                            </div>
                            <span class="offer-users--item__user--icon">
                                <common-icon name="trash" size="medium" @click="removeInvite(user)"></common-icon>
                            </span>
                        </div>
                    </div>
                </div>
            </common-content-spacer>

            <common-button
                class="add-user"
                type="link"
                size="large-long"
                :label="$t('components.availability.add_availability.user_availability.add_user')"
                @click="toggleAddUserModal()"
            ></common-button>
        </div>
        <add-user-modal
            v-if="isAddUserModalOpen"
            :offer="offer"
            :users="users"
            :companyUsers="companyUsers"
            @close="isAddUserModalOpen = false"
            @add-users="addUsers"
            @add-invited-users="addInvitedUsers"
        ></add-user-modal>
    </div>
</template>

<script>
    import AddUserModal from 'components/modals/availability/AddUserModal'
    import CheckboxInput from 'components/form-inputs/CheckboxInput.vue'
    import CompanyAPI from 'api/CompanyAPI'
    import OfferAPI from 'api/OfferAPI'
    import UserAvailabilityEnum from 'enums/user_availability_role.enum'
    import WindowResizeMixin from 'mixins/WindowResizeMixin'

    export default {
        mixins: [WindowResizeMixin],

        components: { AddUserModal, CheckboxInput },
        props: {
            offer: { type: Object, required: true },
            existingAvailability: { type: Object, required: false, default: undefined },
            prefill: { type: Object, required: true },
        },
        data() {
            return {
                isAddUserModalOpen: false,
                users: [],
                selectedUsers: [],
                companyUsers: [],
                inviteUsers: [],
            }
        },

        methods: {
            toggleAddUserModal() {
                this.isAddUserModalOpen = !this.isAddUserModalOpen
            },

            addUsers(payload) {
                payload.forEach((us) => this.selectedUsers.push(us))
                this.selectedUsers.forEach((user) => {
                    if (user.function === UserAvailabilityEnum.PERFORMER) {
                        user.user.isPerformer = true
                        user.user.isModerator = true
                    } else {
                        user.user.isPerformer = false
                        user.user.isModerator = true
                    }
                })
                this.users = this.users.filter((user) => !this.selectedUsers.map((us) => us.user.id).includes(user.user.id))
                this.toggleAddUserModal()
            },

            addInvitedUsers(payload) {
                this.inviteUsers = this.inviteUsers.concat(payload)
                this.toggleAddUserModal()
            },

            toggleUserPerfomer(user) {
                user.user.isPerformer = true
                this.emitEvent({ name: 'toggle-performer', payload: user })
                this.selectedUsers.forEach((us) => {
                    if (us.user.id !== user.user.id) {
                        us.user.isPerformer = false
                    }
                })
            },

            toggleUserModerator(user) {
                user.user.isModerator = !user.user.isModerator
            },

            findCurrentUserFromList(array) {
                return array.find((us) => us.user.id === this.$auth.getUser().id)
            },

            removeAlreadyAddedUsers(array) {
                if (!this.prefill?.availabilityUsers) {
                    return array
                }
                return array.filter(
                    (us) =>
                        us.user.id !== this.$auth.getUser().id &&
                        !this.prefill?.availabilityUsers.map((aus) => aus.user.id).includes(us.user.id),
                )
            },

            getCompany() {
                CompanyAPI.getById(this.offer?.location?.companyServiceProvider?.id).then((response) => {
                    this.companyUsers = response.companyUsers
                    this.users = this.removeAlreadyAddedUsers(response.companyUsers)
                    if (!this.prefill?.availabilityUsers?.length) {
                        const userMe = this.findCurrentUserFromList(response.companyUsers)
                        if (!this.prefill?.availabilityUsers) {
                            userMe.user.isPerformer = true
                            userMe.user.isModerator = true
                        }
                        this.emitEvent({ name: 'toggle-performer', payload: userMe })
                        this.selectedUsers.push(userMe)
                        this.users = this.users.filter((us) => us.user.id !== this.$auth.getUser().id)
                    }
                    this.setPrefillUsers()
                })
            },

            removeInvite(user) {
                if (user.id) {
                    OfferAPI.removeInvite(user.id).then(() => {
                        this.inviteUsers = this.inviteUsers.filter((us) => us.id !== user.id)
                    })
                } else {
                    this.inviteUsers = this.inviteUsers.filter((us) => us.email !== user.email)
                }
            },

            setPrefillUsers() {
                if (!this.prefill?.availabilityUsers?.length) {
                    return
                }
                this.prefill.availabilityUsers.forEach((us) => {
                    if (us.function === UserAvailabilityEnum.PERFORMER) {
                        us.user.isPerformer = true
                        us.user.isModerator = true
                    }
                    if (us.function === UserAvailabilityEnum.MODERATOR) {
                        us.user.isPerformer = false
                        us.user.isModerator = true
                    }
                    this.selectedUsers.push(us)
                })

                this.removeAlreadyAddedUsers(this.selectedUsers)
            },

            getInvitedUsers() {
                if (this.offer?.location?.companyServiceProvider?.id) {
                    this.getCompanyInvites()
                } else {
                    this.getUserInvites()
                }
            },

            getCompanyInvites() {
                OfferAPI.getCompanyInvites(this.offer?.location?.companyServiceProvider?.id, this.prefill?.id).then(
                    (response) => {
                        this.inviteUsers = response
                    },
                )
            },

            getUserInvites() {
                OfferAPI.getUserInvites(this.prefill?.id).then((response) => {
                    this.inviteUsers = response
                })
            },
        },

        mounted() {
            if (this.offer?.location?.companyServiceProvider?.id) {
                this.getCompany()
            } else if (this.prefill?.id) {
                this.getInvitedUsers()
                this.setPrefillUsers()
            } else {
                const userMe = {
                    user: this.$auth.getUser(),
                }
                userMe.user.isPerformer = true
                userMe.user.isModerator = true
                this.selectedUsers.push(userMe)
            }
        },
    }
</script>

<style scoped lang="scss">
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/mixins';

    .offer-users-container {
        width: 500px;
        margin: 0 auto;
        @include breakpoint(smartphone-large) {
            width: 100%;
        }

        .offer-users {
            &--item {
                display: flex;
                align-items: center;
                justify-content: space-between;
                margin-bottom: 10px;

                @include breakpoint(smartphone-large) {
                    flex-direction: column;
                    justify-content: center;
                }

                &__header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    font-weight: 500;
                    font-size: 18px;
                    margin-bottom: 20px;

                    .offer-users--item__user {
                        min-width: 276px;
                    }
                }

                &__user {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding-right: 30px;

                    &--name {
                        width: 186px;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                        overflow: hidden;
                    }

                    &--invite {
                        width: 500px;
                        @include breakpoint(smartphone-large) {
                            width: 100%;
                        }
                    }

                    .avatar {
                        margin-right: 10px;
                    }
                    &--info {
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        @include breakpoint(smartphone-large) {
                            justify-content: space-between;
                            width: 270px;
                            text-overflow: ellipsis;
                            white-space: nowrap;
                            overflow: hidden;
                        }
                    }
                    &--icon {
                        color: $color--accent-4;
                    }
                    .icon {
                        border-radius: 50%;
                        cursor: pointer;
                        width: 20px;
                        height: 20px;
                    }
                }

                &__action {
                    width: 240px;
                    display: flex;
                    justify-content: flex-start;
                    position: relative;
                    padding-left: 30px;

                    @include breakpoint(smartphone-large) {
                        padding-top: 10px;
                        &::after {
                            content: '';
                            position: absolute;
                            background-color: $color--border-dropdown;
                            color: $color--accent-4;
                            width: 100%;
                            height: 2px;
                            top: 75px;
                            right: 5px;
                        }
                    }

                    &:not(:first-child)::before {
                        @include breakpoint(smartphone-large) {
                            display: none;
                        }
                        content: '';
                        position: absolute;
                        background-color: $color--border-dropdown;
                        color: $color--accent-4;
                        width: 2px;
                        height: 80px;
                        top: -5px;
                        left: 0;
                    }

                    p {
                        width: 120px;
                    }
                }
            }
        }

        .add-user {
            margin-top: 30px;
            @include breakpoint(tablet-small) {
                margin-left: 150px;
            }
            @include breakpoint(smartphone-large) {
                margin-left: 50px;
            }
        }
    }
</style>
